/**
 * Web Push subscription helper.
 *
 * Talks to the browser PushManager + our /api/me/push endpoints.
 *
 * Lifecycle:
 *   1. registerServiceWorker() — call once at app boot. No-op if SW
 *      is already registered.
 *   2. getPushState() — read current permission + subscription state
 *      so the UI can decide whether to nudge the user.
 *   3. subscribeUserToPush() — prompt for permission, subscribe with
 *      our VAPID public key, POST the subscription to the backend.
 *   4. unsubscribeUserFromPush() — opt-out flow.
 *
 * Handles the modern Promise-based browser API; gracefully skips when
 * the user-agent doesn't support Push (older Safari, in-app webviews).
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushIsSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    return reg;
  } catch (err) {
    console.warn("SW register failed:", err);
    return null;
  }
}

export async function getPushState() {
  if (!pushIsSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    subscribed = !!sub;
  } catch { /* ignore */ }
  return { supported: true, permission, subscribed };
}

export async function subscribeUserToPush() {
  if (!pushIsSupported()) return { ok: false, reason: "unsupported" };
  // Browser prompt — must be in user-gesture context.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };

  let publicKey;
  try {
    const r = await api.get("/me/push/vapid-public-key", { silent: true });
    publicKey = r.data?.public_key;
  } catch (err) {
    return { ok: false, reason: "vapid-fetch-failed", error: err };
  }
  if (!publicKey) return { ok: false, reason: "no-vapid-key" };

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  await api.post("/me/push/subscribe", {
    endpoint: sub.endpoint,
    keys: {
      p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
      auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
    },
    user_agent: navigator.userAgent.slice(0, 240),
  });
  return { ok: true };
}

export async function unsubscribeUserFromPush() {
  if (!pushIsSupported()) return { ok: false };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post("/me/push/unsubscribe", { endpoint: sub.endpoint });
      await sub.unsubscribe();
    }
  } catch (err) {
    return { ok: false, error: err };
  }
  return { ok: true };
}

export async function sendTestPush() {
  return api.post("/me/push/test");
}
